import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthDialog = ({ open, onOpenChange, onSuccess }) => {
  const { t } = useLang();
  const { register, login } = useAuth();
  const [mode, setMode] = useState("register"); // register | login | forgot
  const [form, setForm] = useState({ first_name: "", email: "", password: "", phone: "" });
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setErr(""); setInfo(""); };

  const submit = async () => {
    reset(); setBusy(true);
    try {
      if (mode === "register") {
        const u = await register(form);
        onSuccess?.(u); onOpenChange(false);
      } else if (mode === "login") {
        const u = await login(form.email, form.password);
        onSuccess?.(u); onOpenChange(false);
      } else {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email: form.email });
        setInfo(data.dev_reset_link ? `${t("auth.resetSent")} (dev link logged)` : t("auth.resetSent"));
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <div className="mb-1 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground"><ShieldCheck className="h-5 w-5" /></div>
            <DialogTitle className="font-serif text-2xl">
              {mode === "register" ? t("auth.createTitle") : mode === "login" ? t("auth.signinTitle") : t("auth.forgotTitle")}
            </DialogTitle>
          </div>
          <DialogDescription>
            {mode === "register" ? t("auth.createDesc") : mode === "login" ? t("auth.signinDesc") : t("auth.forgotDesc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {mode === "register" && (
            <div>
              <Label className="text-xs">{t("auth.firstName")}</Label>
              <Input data-testid="auth-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </div>
          )}
          <div>
            <Label className="text-xs">{t("auth.email")}</Label>
            <Input data-testid="auth-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          {mode !== "forgot" && (
            <div>
              <Label className="text-xs">{t("auth.password")}</Label>
              <Input data-testid="auth-password" type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && submit()} />
            </div>
          )}
          {mode === "register" && (
            <div>
              <Label className="text-xs">{t("auth.phoneOpt")}</Label>
              <Input data-testid="auth-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          )}
          {err && <p className="text-sm text-destructive" data-testid="auth-error">{err}</p>}
          {info && <p className="text-sm text-emerald-600" data-testid="auth-info">{info}</p>}

          <Button className="w-full gap-2" data-testid="auth-submit" onClick={submit} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "register" ? t("auth.createBtn") : mode === "login" ? t("auth.signinBtn") : t("auth.forgotBtn")}
          </Button>

          <div className="flex items-center justify-between pt-1 text-xs">
            {mode === "register" ? (
              <button data-testid="auth-switch-login" className="text-primary hover:underline" onClick={() => { reset(); setMode("login"); }}>{t("auth.haveAccount")}</button>
            ) : (
              <button data-testid="auth-switch-register" className="text-primary hover:underline" onClick={() => { reset(); setMode("register"); }}>{t("auth.noAccount")}</button>
            )}
            {mode !== "forgot" && (
              <button data-testid="auth-forgot-link" className="text-muted-foreground hover:underline" onClick={() => { reset(); setMode("forgot"); }}>{t("auth.forgotLink")}</button>
            )}
          </div>
          <p className="pt-1 text-center text-[11px] text-muted-foreground">{t("auth.noCard")}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
